import type { PageHierarchyNode } from '@integration-layer/ampliance/schemas/page-hierarchy-node-schema.localized'
import type { PlpPage } from '@integration-layer/ampliance/schemas/plp-page-schema.localized'
import type { EditorialPage } from '@integration-layer/ampliance/schemas/editorial-page-schema.localized'
import type { Eligible } from '@integration-layer/ampliance/schemas/eligible-schema.localized'

import type { H3Event, EventHandlerRequest } from 'h3'
import { setHeader } from 'h3'

import { isProductRoute } from '@integration-layer/utils/url'
import {
  hasEligibleAccess,
  isOpenToRegisteredCustomers,
  parseEligibleTags,
} from '@integration-layer/utils/privateSaleEligibility'
import { getAmpliencePageFetchKey } from '@/utils/amplienceFetchKey'

type CmsPageData = PageHierarchyNode | null | undefined

/**
 * Applies header transparency, megalogo, moodboard and homepage flags from CMS data.
 * Independent of eligibility checks — safe to run immediately after main CMS fetch.
 */
function applyAmpliencePageConfig(
  app: ReturnType<typeof useNuxtApp>,
  data: CmsPageData
) {
  app.payload['has-plp-moodboard'] =
    data?.schema === 'https://armani.com/plp-page' &&
    // @ts-ignore
    data?.content?.at(0)?.logicWrapper?.bandLogic?.length > 0

  let hasPageTransparentHeader = false
  let hasPageMegalogo = false
  const pagesWithTransparentHeader = ['https://armani.com/brand-homepage']

  if (data?.schema === 'https://armani.com/plp-page') {
    // @ts-ignore
    hasPageTransparentHeader = !!data?.content?.at(0)?.bannerImage
  } else if (data?.schema === 'https://armani.com/editorial-page') {
    // @ts-ignore
    hasPageTransparentHeader =
      // @ts-ignore
      data?.content?.at(0)?.headerTransparent ?? true
  } else {
    hasPageTransparentHeader = pagesWithTransparentHeader.includes(
      data?.schema ?? ''
    )
  }

  if (hasPageTransparentHeader) {
    // @ts-ignore
    hasPageMegalogo = !!data?.content?.at(0)?.showMegalogo
  }

  if (
    data?.schema === 'https://armani.com/crossbrand-homepage-newest' &&
    // @ts-ignore
    data?.content?.[0]?.editorialValuesTop
  ) {
    app.payload['hp_has_editorial_values_top'] = true
  }

  app.payload['header_config'] = {
    megalogo: hasPageMegalogo,
    transparency: hasPageTransparentHeader,
    // @ts-ignore
    color_transparency: data?.content?.at(0)?.headerTextColor,
  }
}

/**
 * Adds cache control headers to the server response for improved performance and caching.
 *
 * @param {H3Event<EventHandlerRequest>} event - The H3 event object representing the server request.
 * @param {string} [vary='query'] - Optional vary header to control cache variation.
 * @description Sets Netlify-specific cache headers for public, server-side caching with configurable max-age and stale-while-revalidate.
 * @private
 */
async function addCacheHeaders(
  event: H3Event<EventHandlerRequest>,
  vary?: string
) {
  if (import.meta.server) {
    const CACHE_AGE = 3600 * 12
    const STALE_WHILE_REVALIDATE = 3600 * 24
    setHeader(event, 'Cache-Control', 'public, max-age=0, must-revalidate')
    setHeader(event, 'Netlify-Vary', vary ?? 'query')
    setHeader(
      event,
      'Netlify-CDN-Cache-Control',
      `public, max-age=${CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
    )
  }
}

/**
 * Nuxt route middleware for handling page fetching and configuration.
 *
 * @description Manages page routing, content fetching from CMS, and setting page-specific configurations.
 * @returns {Promise<void>}
 *
 * @remarks
 * This middleware performs several key functions:
 * 1. Handles routing for localized pages
 * 2. Fetches page content from CMS
 * 3. Sets page type and header configurations
 * 4. Manages caching and user session
 *
 * @throws {Error} Throws a 404 error for invalid or non-existent pages
 */
export default defineNuxtRouteMiddleware(async to => {
  // Skip middleware for federated environments
  if (useRuntimeConfig().public.isFederated) return

  const app = useNuxtApp()

  // Handle invalid slug routes
  if (to.name === 'slug') {
    // at this point in time, edge functions didn't find any redirect
    // netlify didnt find any static file
    // so we have a impossible to be present slug page (since we always want localized-slug)
    throw createError({
      statusCode: 404,
    })
  }

  // Reset page configurations when navigating away from Amplience pages
  if (
    !(
      (to.name as string).startsWith('localized-slug') ||
      (to.name as string) === 'localized-index'
    )
  ) {
    app.payload['amplience_page_type'] = null
    app.payload['is_product'] = false
    app.payload['header_config'] = {
      megalogo: false,
      transparency: false,
    }
    return
  }

  const isProduct = isProductRoute(to)
  if (isProduct) {
    app.payload['amplience_page_type'] = null
    app.payload['has-plp-moodboard'] = false
    app.payload['is_product'] = true
    app.payload['header_config'] = {
      megalogo: false,
      transparency: false,
    }
    const event = useRequestEvent()
    await addCacheHeaders(event!, 'query, header=x-datadog-test')
    if (import.meta.server) {
      const userSession = await app.runWithContext(useUserSession)
      try {
        await userSession.clear()
      } catch {
        await $fetch('/api/_auth/session', { method: 'DELETE' })
        // @ts-expect-error
        userSession.session.value = {}
      }
      app.payload.isCached = true
    }
    return
  }

  app.payload['is_product'] = false

  /**
   * Generates a delivery key for CMS content retrieval based on the current route.
   *
   * @returns {string} A formatted delivery key with locale and country.
   * @private
   */
  const getDeliveryKey = () => {
    const segments = to.path.split('/').slice(1)
    const [loc, country] = segments[0].split('-')
    segments[0] = `${loc?.toLowerCase()}-${country?.toUpperCase()}`
    return segments.join('/')
  }
  const deliveryKey = getDeliveryKey()
  const vse = to.query.vse! as string

  const locale = to.path.split('/')[1]
  const url = `/api/cms/getByKey?locale=${locale}&id=${deliveryKey}&type=key&vse=${vse ?? ''}`

  const { data, error } = await useFetch<PageHierarchyNode>(url, {
    key: getAmpliencePageFetchKey(deliveryKey, locale, vse),
  })

  // Handle CMS fetch errors
  if (error.value) {
    if (import.meta.server) {
      throw createError({
        statusCode: 404,
        fatal: true,
      })
    }
    if (import.meta.client) {
      app.payload['middleware_error'] = true
    }
  }
  // Set page type from CMS schema
  app.payload['amplience_page_type'] = data.value?.schema

  applyAmpliencePageConfig(app, data.value)

  let isEligibleGatedPage = false

  if (
    data.value?.schema === 'https://armani.com/plp-page' ||
    data.value?.schema === 'https://armani.com/editorial-page'
  ) {
    // @ts-ignore
    const hasEligibleContent = data.value?.content?.at(0)?.showForEligible
    if (hasEligibleContent) {
      isEligibleGatedPage = true
      const { user } = useUserSession()

      // Se non è loggato, redirect a signup
      if (!user.value || !user.value.user_metadata?.commercelayer_id) {
        const currentUrl = to.fullPath
        const localePath = useLocalePath()
        const currentPageBeforeLogin = useCookie('page-before-login', {
          domain: import.meta.dev ? 'localhost' : '.armani.com',
        })
        currentPageBeforeLogin.value = currentUrl

        return navigateTo({
          path: localePath('/signup'),
          query: {
            callback: currentUrl,
            content: hasEligibleContent,
          },
        })
      }

      const eligibleContentUrl = `/api/cms/getByKey?locale=${locale}&id=${hasEligibleContent}&type=key&vse=${vse ?? ''}`
      const eligibleFetchKey = getAmpliencePageFetchKey(
        hasEligibleContent,
        locale,
        vse
      )

      const sessionTags = user.value.user_metadata?.customer_tags || []
      const commerceLayerId = user.value.user_metadata?.commercelayer_id

      const { data: contentFromSchema } = await useFetch<Eligible>(
        eligibleContentUrl,
        { key: eligibleFetchKey }
      )
      const eligibleTags = parseEligibleTags(contentFromSchema.value?.tags)

      if (!eligibleTags.length) {
        return
      }

      // registered only needs the logged-in customer already verified above
      if (isOpenToRegisteredCustomers(eligibleTags)) {
        return
      }

      const customerTags = await $fetch<string[]>(
        `/api/cl/getCustomerTags/${commerceLayerId}`
      ).catch(() => sessionTags)

      if (
        !hasEligibleAccess({
          eligibleTags,
          customerTags,
          isLogged: true,
        })
      ) {
        throw showError({
          statusCode: 404,
          message: `deliveryKey-${hasEligibleContent}`,
        })
      }
    }
  }

  // Add cache headers
  const event = useRequestEvent()
  const vary =
    data.value?.schema === 'https://armani.com/plp-page'
      ? `cookie=${useAppConfig().website}-grid-layout-cookies,query,header=x-datadog-test`
      : 'query,header=x-datadog-test'

  // Gated pages are rendered for a specific customer: never cache them on CDN,
  // otherwise the SSR HTML would be served to non-eligible/anonymous users
  if (isEligibleGatedPage) {
    if (import.meta.server) {
      setHeader(event!, 'Cache-Control', 'private, no-cache, no-store')
      setHeader(event!, 'Netlify-CDN-Cache-Control', 'no-store')
    }
  } else {
    await addCacheHeaders(event!, vary)
  }
  if (import.meta.server) {
    const userSession = await app.runWithContext(useUserSession)
    try {
      await userSession.clear()
    } catch {
      await $fetch('/api/_auth/session', { method: 'DELETE' })
      // @ts-expect-error
      userSession.session.value = {}
    }
    app.payload.isCached = true
  }
})
